import {DocumentNode, gql} from '@apollo/client';
import {entry, objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {fillPlanMethodVersionWithPlannedValues} from 'procedures/utils/fillPlanMethodVersionWithPlannedValues';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRun} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  getValuesForMethod,
  prepareInitialExperimentalInputs,
  ProcedureMethodDefinitionVersionNodeParameters,
} from 'studies/typed/shared/ExperimentalInputs';

import {GetStudyDesignFromProcedureRunGraphQL as ProcedureRunNode} from './__generated/GetStudyDesignFromProcedureRunGraphQL';

interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
}

/**
 * Extracts the study design from a ProcedureRunNode.
 *
 * @param procedureRun - a ProcedureRunNode that contains STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT
 * @returns an object of {experimentalInputs, templateMethods, templateFlowchart} for use with <ProcedureRunPlan>.
 */
export default function getStudyDesignFromProcedureRun(procedureRun: ProcedureRunNode): StudyDesignParams {
  // This is the flowchart version that corresponds to the procedure the user selected to initiate the experiment.
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');
  const nodes = flowchartVersion.nodes;
  // The <ProcedureRunPlan> should thus render the ordered methods of this flowchart.
  const templateMethods: Array<ExperimentMethod> = sortNodesByCoordinates(
    [...nodes],
    'gridLayoutCoordinates'
  );

  const templateFlowchart = {...flowchartVersion, nodes: templateMethods};

  // The corresponding ExperimentalInputs data structure maps the flowchart's method ids to metadata and
  // values for each condition. Each PMEI has its own method, a pointer to the original procedure's flowchart
  // method, and a condition (replicate) that is associated with....
  const planMethodByTemplateMethodId: {
    [methodId: string]: {[conditionId: string]: ProcedureMethodDefinitionVersionNodeParameters};
  } = {};
  procedureRun.methodExecutionInstances.forEach((pmei) => {
    const templateMethodVersionId = getAccessibleOrInaccessibleId(pmei.templateMethodVersion);
    const method = pmei.planMethodDefinitionVersion;
    planMethodByTemplateMethodId[templateMethodVersionId] ??= {};
    planMethodByTemplateMethodId[templateMethodVersionId][
      pmei.procedureRunConditionReplicate.procedureRunCondition.id
    ] = fillPlanMethodVersionWithPlannedValues(
      method,
      pmei.dataParameterPlannedValues,
      pmei.materialInputPlannedValues,
      pmei.equipmentPlannedValues,
      // For process intermediates, we do want to include the fallback template info
      {fallbackToPlanMaterialInputs: true}
    );
  });
  // ...which is the necessary information to reconstitute the ExperimentalInputs[methodId].values.
  const planValues = objectFromEntries(
    templateMethods.map((methodNode) =>
      entry(methodNode.id, {
        values: procedureRun.conditions.map((condition) =>
          getValuesForMethod(planMethodByTemplateMethodId[methodNode.id][condition.id])
        ),
      })
    )
  );
  // Parameter confirmation methods might have changed in planning, so extract those too.
  const prevConfirmationMethods = objectFromEntries(
    templateMethods.map((methodNode) =>
      entry(methodNode.id, {
        parameterConfirmationMethods: getPlannedParameterConfirmationMethods(
          methodNode,
          planMethodByTemplateMethodId
        ),
      })
    )
  );
  // Finally, we can construct the full ExperimentalInputs data structure, seeded with the planValues and
  // plan confirmation methods
  const experimentalInputs = prepareInitialExperimentalInputs(
    procedureRun.conditions.length,
    templateMethods,
    {prevExperimentalInputValues: planValues, prevConfirmationMethods}
  );

  return {experimentalInputs, templateFlowchart};
}

function getPlannedParameterConfirmationMethods(
  templateMethod: ExperimentMethod,
  planMethodByTemplateMethodId: {
    [methodId: string]: {[conditionId: string]: ProcedureMethodDefinitionVersionNodeParameters};
  }
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  const planMethods = Object.values(planMethodByTemplateMethodId[templateMethod.id]);

  // We expect that all plan methods have the same parameter confirmation methods, even if they may differ
  // from the template method.
  assert(planMethods.length >= 1, 'Expected at least 1 matching planned method');
  const referencePlanMethod = planMethods[0];
  planMethods.forEach((planMethod) => {
    assert(
      planMethod.dataParameterSettings.length === templateMethod.dataParameterSettings.length,
      'Expected planned method to have same number of data parameters as template method'
    );
    planMethod.dataParameterSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.dataParameterSettings[i].version.name,
        'Expected planned method to have same order of data parameters as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.dataParameterSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same parameter confirmation methods'
      );
    });
    assert(
      planMethod.materialInputSettings.length === templateMethod.materialInputSettings.length,
      'Expected planned method to have same number of material inputs as template method'
    );
    planMethod.materialInputSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.materialInputSettings[i].version.name,
        'Expected planned method to have same order of material inputs as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.materialInputSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same material confirmation methods'
      );
    });
    assert(
      planMethod.equipmentSettings.length === templateMethod.equipmentSettings.length,
      'Expected planned method to have same number of equipment as template method'
    );
    planMethod.equipmentSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.equipmentSettings[i].version.name,
        'Expected planned method to have same order of equipment as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.equipmentSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same equipment confirmation methods'
      );
    });
  });

  // Extract the planned confirmation methods from the planned methods
  return {
    parameters: templateMethod.dataParameterSettings.map(
      (_, i) => referencePlanMethod.dataParameterSettings[i].version.confirmationMethod
    ),
    materialInputs: templateMethod.materialInputSettings.map(
      (_, i) => referencePlanMethod.materialInputSettings[i].version.confirmationMethod
    ),
    equipment: templateMethod.equipmentSettings.map(
      (_, i) => referencePlanMethod.equipmentSettings[i].version.confirmationMethod
    ),
  };
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunGraphQL on ProcedureRunNode {
    id
    name
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        ...ExperimentalInputsFlowchartGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    procedureFlowchartConfigVersionProxy {
      ...ProcedureFlowchartConfigVersionProxyAllFieldsGraphQL
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    methodExecutionInstances {
      ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
      planMethodDefinitionVersion {
        ...BaseProcedureMethodDefinitionVersionGraphQL
        materialInputSettings {
          templateValues {
            id
            material
            amount
          }
        }
      }
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      procedureRunConditionReplicate {
        id
        procedureRunCondition {
          id
        }
      }
    }
    conditions {
      id
      procedureRunConditionReplicates {
        id
      }
    }
  }
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('BaseProcedureMethodDefinitionVersion').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartConfigVersionProxyAllFields').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
`;
